import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { TakeOffPayload, Exposition, countryFlag } from './types'
import { Table, TableBody, TableCell, TableRow } from '../components/table'
import { Button } from '../components/button'
import { PlusIcon, FunnelIcon, XMarkIcon, MapPinIcon } from '@heroicons/react/20/solid'
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from '../components/dialog'
import { useAuthStatus } from './user_dropdown'
import { AirtimeMap, Source, Layer, NavigationControl, type MapLayerMouseEvent, type MapRef } from '../components/airtime_map'
import DualRangeSlider from './dual_range_slider'

const ALL_EXPOSITIONS: Exposition[] = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW']
const ALTITUDE_MIN = 0
const ALTITUDE_MAX = 4000
const ALTITUDE_STEP = 50
const EXPOSITION_ANGLE: Record<Exposition, number> = {
  N: 0, NE: 45, E: 90, SE: 135, S: 180, SW: 225, W: 270, NW: 315,
}

function wedgePath(centreDeg: number, span: number, r: number): string {
  const half = span / 2
  const startDeg = centreDeg - half
  const endDeg = centreDeg + half
  const toRad = (d: number) => ((d - 90) * Math.PI) / 180
  const sx = r * Math.cos(toRad(startDeg))
  const sy = r * Math.sin(toRad(startDeg))
  const ex = r * Math.cos(toRad(endDeg))
  const ey = r * Math.sin(toRad(endDeg))
  const large = span > 180 ? 1 : 0
  return `M0,0 L${sx},${sy} A${r},${r} 0 ${large} 1 ${ex},${ey} Z`
}

function MiniExposition({ expositions }: { expositions: Exposition[] }) {
  const active = new Set(expositions)
  const r = 50
  const size = 110
  return (
    <svg
      viewBox={`${-size / 2} ${-size / 2} ${size} ${size}`}
      className="block"
      style={{ width: 24, height: 24 }}
      aria-label="Expositions"
    >
      {ALL_EXPOSITIONS.map(exp => (
        <path
          key={exp}
          d={wedgePath(EXPOSITION_ANGLE[exp], 45, r)}
          className={`stroke-white dark:stroke-[#2D3142] ${
            active.has(exp) ? 'fill-[#EF8354]' : 'fill-[#BFC0C0]/20 dark:fill-[#4F5D75]/30'
          }`}
          strokeWidth={2}
        />
      ))}
      <circle r={3} className="fill-[#BFC0C0]/40 dark:fill-[#4F5D75]/40" />
    </svg>
  )
}

/* ── Takeoff filter types ─────────────────────────────────────────── */

interface TakeoffFilterState {
  expositions: Set<Exposition>
  minAltitude: string
  maxAltitude: string
}

const defaultTakeoffFilters = (): TakeoffFilterState => ({
  expositions: new Set<Exposition>(ALL_EXPOSITIONS),
  minAltitude: '',
  maxAltitude: '',
})

function hasTakeoffFilters(f: TakeoffFilterState): boolean {
  return f.expositions.size < ALL_EXPOSITIONS.length
    || f.minAltitude !== ''
    || f.maxAltitude !== ''
}

function buildTakeoffParams(f: TakeoffFilterState): string {
  const params = new URLSearchParams()
  if (f.expositions.size > 0 && f.expositions.size < ALL_EXPOSITIONS.length) {
    params.set('expositions', [...f.expositions].join(','))
  }
  if (f.minAltitude) params.set('min_altitude', f.minAltitude)
  if (f.maxAltitude) params.set('max_altitude', f.maxAltitude)
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

/* ── Interactive exposition quadrant for the filter panel ────────── */

function ExpositionFilter({
  selected,
  onToggle,
}: {
  selected: Set<Exposition>
  onToggle: (exp: Exposition) => void
}) {
  const r = 50
  const size = 110
  const labelR = r + 12
  return (
    <svg
      viewBox={`${-size / 2} ${-size / 2} ${size} ${size}`}
      className="block"
      style={{ width: 96, height: 96 }}
      aria-label="Filter by exposition"
    >
      {ALL_EXPOSITIONS.map(exp => {
        const angle = EXPOSITION_ANGLE[exp]
        const toRad = (d: number) => ((d - 90) * Math.PI) / 180
        const lx = labelR * Math.cos(toRad(angle))
        const ly = labelR * Math.sin(toRad(angle))
        return (
          <g key={exp} className="cursor-pointer" onClick={() => onToggle(exp)}>
            <path
              d={wedgePath(angle, 45, r)}
              className={`stroke-white dark:stroke-[#2D3142] transition-colors ${
                selected.has(exp) ? 'fill-[#EF8354]' : 'fill-[#BFC0C0]/20 dark:fill-[#4F5D75]/30'
              }`}
              strokeWidth={2}
            />
            <text
              x={lx}
              y={ly}
              textAnchor="middle"
              dominantBaseline="central"
              className={`text-[8px] font-medium select-none pointer-events-none ${
                selected.has(exp)
                  ? 'fill-[#EF8354]'
                  : 'fill-[#4F5D75] dark:fill-[#BFC0C0]'
              }`}
            >
              {exp}
            </text>
          </g>
        )
      })}
      <circle r={3} className="fill-[#BFC0C0]/40 dark:fill-[#4F5D75]/40" />
    </svg>
  )
}

/* ── Helpers ──────────────────────────────────────────────────────── */

/** Parse a "lat,lon" coordinate string into [lat, lon] or null. */
function parseCoords(s: string): [number, number] | null {
  const parts = s.split(',')
  if (parts.length !== 2) return null
  const lat = parseFloat(parts[0])
  const lon = parseFloat(parts[1])
  if (isNaN(lat) || isNaN(lon)) return null
  return [lat, lon]
}

/** Build a GeoJSON FeatureCollection from takeoffs. */
function toGeoJSON(takeoffs: TakeOffPayload[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: takeoffs.flatMap(t => {
      const c = parseCoords(t.coordinates)
      if (!c) return []
      return [{
        type: 'Feature' as const,
        geometry: { type: 'Point' as const, coordinates: [c[1], c[0]] },
        properties: { id: t.id, name: t.name },
      }]
    }),
  }
}

interface MapBounds {
  north: number
  south: number
  east: number
  west: number
}

function isInBounds(coords: string, bounds: MapBounds): boolean {
  const c = parseCoords(coords)
  if (!c) return false
  const [lat, lon] = c
  return lat >= bounds.south && lat <= bounds.north
    && lon >= bounds.west && lon <= bounds.east
}

function AddTakeoffModal({ open, onClose, onCreated }: {
  open: boolean
  onClose: () => void
  onCreated: (name: string, id: number) => void
}) {
  const [lat, setLat] = useState(46.5)
  const [lon, setLon] = useState(6.5)
  const [name, setName] = useState('')
  const [saving, setSaving] = useState(false)
  const nameRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) {
      setName('')
      setLat(46.5)
      setLon(6.5)
      setTimeout(() => nameRef.current?.focus(), 100)
    }
  }, [open])

  const pointData = {
    type: 'Feature' as const,
    geometry: { type: 'Point' as const, coordinates: [lon, lat] },
    properties: {},
  }

  const handleSubmit = async () => {
    if (!name.trim()) return
    setSaving(true)
    try {
      const coords = `${lat.toFixed(6)},${lon.toFixed(6)}`
      const res = await fetch('/v0/takeoffs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), coordinates: coords }),
      })
      if (!res.ok) return
      const { id } = await res.json()
      onCreated(name.trim(), id)
      onClose()
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Add Takeoff</DialogTitle>
      <DialogDescription>Click on the map to set the location, then give it a name.</DialogDescription>
      <DialogBody>
        <div className="h-72 rounded-lg overflow-hidden mb-3">
          <AirtimeMap
            initialViewState={{ longitude: lon, latitude: lat, zoom: 8 }}
            style={{ width: '100%', height: '100%' }}
            onClick={e => { setLat(e.lngLat.lat); setLon(e.lngLat.lng) }}
            cursor="crosshair"
          >
            <Source id="new-takeoff-src" type="geojson" data={pointData}>
              <Layer
                id="new-takeoff-circle"
                type="circle"
                paint={{
                  'circle-radius': 10,
                  'circle-color': '#EF8354',
                  'circle-stroke-color': '#ffffff',
                  'circle-stroke-width': 2,
                }}
              />
            </Source>
          </AirtimeMap>
        </div>
        <p className="mb-3 text-xs text-[#4F5D75] dark:text-[#BFC0C0]">
          {lat.toFixed(6)}, {lon.toFixed(6)}
        </p>
        <input
          ref={nameRef}
          type="text"
          placeholder="Takeoff name"
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleSubmit() }}
          className="w-full rounded-md border border-[#BFC0C0]/50 dark:border-[#4F5D75]/50 bg-transparent px-3 py-2 text-sm text-[#2D3142] dark:text-white outline-none focus:border-[#EF8354] transition-colors"
        />
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose} disabled={saving}>Cancel</Button>
        <Button color="airtime" onClick={handleSubmit} disabled={!name.trim() || saving}>
          {saving ? 'Saving…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export function TakeoffList() {
  const navigate = useNavigate()
  const { userData } = useAuthStatus()
  const [allTakeoffs, setAllTakeoffs] = useState<TakeOffPayload[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [addOpen, setAddOpen] = useState(false)
  const [filterOpen, setFilterOpen] = useState(false)
  const [filters, setFilters] = useState<TakeoffFilterState>(defaultTakeoffFilters)
  const [restrictToMap, setRestrictToMap] = useState(false)
  const [mapBounds, setMapBounds] = useState<MapBounds | null>(null)
  const mapRef = useRef<MapRef>(null)

  const fetchTakeoffs = useCallback(() => {
    setIsLoading(true)
    fetch(`/v0/takeoffs${buildTakeoffParams(filters)}`)
      .then(r => r.json())
      .then(data => setAllTakeoffs(data.takeoffs ?? []))
      .finally(() => setIsLoading(false))
  }, [filters])

  useEffect(() => { fetchTakeoffs() }, [fetchTakeoffs])

  // Derive visible takeoffs: apply map-bounds filter client-side
  const takeoffs = useMemo(() => {
    if (!restrictToMap || !mapBounds) return allTakeoffs
    return allTakeoffs.filter(t => isInBounds(t.coordinates, mapBounds))
  }, [allTakeoffs, restrictToMap, mapBounds])

  // GeoJSON for map markers (always show all fetched takeoffs on the map)
  const geojson = useMemo(() => toGeoJSON(allTakeoffs), [allTakeoffs])

  const updateBoundsFromMap = useCallback(() => {
    const map = mapRef.current
    if (!map) return
    const b = map.getBounds()
    if (!b) return
    setMapBounds({
      north: b.getNorth(),
      south: b.getSouth(),
      east: b.getEast(),
      west: b.getWest(),
    })
  }, [])

  const handleMapLoad = useCallback(() => {
    updateBoundsFromMap()
  }, [updateBoundsFromMap])

  const handleMoveEnd = useCallback(() => {
    updateBoundsFromMap()
  }, [updateBoundsFromMap])

  const handleMapClick = useCallback((e: MapLayerMouseEvent) => {
    const features = e.features
    if (features && features.length > 0) {
      const name = features[0].properties?.name
      if (name) {
        navigate(`/takeoff/${encodeURIComponent(name)}`)
      }
    }
  }, [navigate])

  const toggleExposition = (exp: Exposition) => {
    setFilters(prev => {
      const next = new Set(prev.expositions)
      if (next.has(exp)) {
        next.delete(exp)
      } else {
        next.add(exp)
      }
      return { ...prev, expositions: next }
    })
  }

  const handleClear = () => setFilters(defaultTakeoffFilters())

  const isFiltered = hasTakeoffFilters(filters)

  const missingExpos = ALL_EXPOSITIONS.filter(e => !filters.expositions.has(e))

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 text-[#2D3142] dark:text-white">Takeoffs</h1>
          <p className="mt-2 text-sm text-[#4F5D75] dark:text-[#BFC0C0]">
            Known takeoff sites.
          </p>
        </div>
        <div className="flex items-center gap-2 mt-4 sm:mt-0 sm:ml-4">
          <button
            onClick={() => setFilterOpen(v => !v)}
            title="Filter takeoffs"
            className={`p-2 rounded-lg transition-colors ${
              isFiltered || filterOpen
                ? 'bg-[#EF8354]/10 text-[#EF8354]'
                : 'text-[#4F5D75] hover:bg-[#BFC0C0]/20 dark:text-[#BFC0C0] dark:hover:bg-[#4F5D75]/20'
            }`}
          >
            <FunnelIcon className="h-5 w-5" />
          </button>
          {userData.authenticated && (
            <Button color="airtime" onClick={() => setAddOpen(true)}>
              <PlusIcon data-slot="icon" />
              Add Takeoff
            </Button>
          )}
        </div>
      </div>

      {/* ── Filter panel ──────────────────────────────────────── */}
      {filterOpen && (
        <div className="mt-4 p-4 rounded-xl bg-white/80 dark:bg-[#2D3142]/60 border border-[#BFC0C0]/40 dark:border-[#4F5D75]/30 shadow-sm space-y-4">
          <div>
            <label className="block text-xs font-medium text-[#4F5D75] dark:text-[#BFC0C0] mb-2">Expositions</label>
            <div className="flex items-center gap-4">
              <ExpositionFilter selected={filters.expositions} onToggle={toggleExposition} />
              <div className="flex flex-wrap gap-1">
                <button
                  onClick={() => setFilters(prev => ({ ...prev, expositions: new Set(ALL_EXPOSITIONS) }))}
                  className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                    filters.expositions.size === ALL_EXPOSITIONS.length
                      ? 'bg-[#EF8354] text-white'
                      : 'bg-[#BFC0C0]/20 text-[#4F5D75] hover:bg-[#EF8354]/10 hover:text-[#EF8354] dark:bg-[#4F5D75]/20 dark:text-[#BFC0C0] dark:hover:bg-[#EF8354]/15 dark:hover:text-[#EF8354]'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setFilters(prev => ({ ...prev, expositions: new Set<Exposition>() }))}
                  className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                    filters.expositions.size === 0
                      ? 'bg-[#EF8354] text-white'
                      : 'bg-[#BFC0C0]/20 text-[#4F5D75] hover:bg-[#EF8354]/10 hover:text-[#EF8354] dark:bg-[#4F5D75]/20 dark:text-[#BFC0C0] dark:hover:bg-[#EF8354]/15 dark:hover:text-[#EF8354]'
                  }`}
                >
                  None
                </button>
              </div>
            </div>
          </div>
          <DualRangeSlider
            label="Altitude"
            min={ALTITUDE_MIN}
            max={ALTITUDE_MAX}
            step={ALTITUDE_STEP}
            lowValue={filters.minAltitude ? Number(filters.minAltitude) : ALTITUDE_MIN}
            highValue={filters.maxAltitude ? Number(filters.maxAltitude) : ALTITUDE_MAX}
            onLowChange={v => setFilters(prev => ({ ...prev, minAltitude: v === ALTITUDE_MIN ? '' : String(v) }))}
            onHighChange={v => setFilters(prev => ({ ...prev, maxAltitude: v === ALTITUDE_MAX ? '' : String(v) }))}
            formatLabel={v => v >= 1000 ? `${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}k` : String(v)}
            unit="m"
          />
          <div className="flex justify-end">
            <button
              onClick={handleClear}
              className="px-3 py-1.5 text-sm text-[#4F5D75] dark:text-[#BFC0C0] hover:text-[#2D3142] dark:hover:text-white transition-colors"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* ── Filter chips (collapsed) ─────────────────────────── */}
      {isFiltered && !filterOpen && (
        <div className="flex flex-wrap items-center gap-2 mt-4">
          {missingExpos.length > 0 && missingExpos.length < ALL_EXPOSITIONS.length && (
            <span className="inline-flex items-center gap-1 rounded-full bg-[#EF8354]/10 px-2.5 py-0.5 text-xs font-medium text-[#EF8354]">
              Expositions: {[...filters.expositions].join(', ')}
              <button onClick={() => setFilters(prev => ({ ...prev, expositions: new Set(ALL_EXPOSITIONS) }))} className="hover:text-[#EF8354]/60">
                <XMarkIcon className="h-3 w-3" />
              </button>
            </span>
          )}
          {filters.expositions.size === 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-[#EF8354]/10 px-2.5 py-0.5 text-xs font-medium text-[#EF8354]">
              No expositions
              <button onClick={() => setFilters(prev => ({ ...prev, expositions: new Set(ALL_EXPOSITIONS) }))} className="hover:text-[#EF8354]/60">
                <XMarkIcon className="h-3 w-3" />
              </button>
            </span>
          )}
          {(filters.minAltitude || filters.maxAltitude) && (
            <span className="inline-flex items-center gap-1 rounded-full bg-[#EF8354]/10 px-2.5 py-0.5 text-xs font-medium text-[#EF8354]">
              {filters.minAltitude && filters.maxAltitude
                ? `${filters.minAltitude}–${filters.maxAltitude}m`
                : filters.minAltitude
                ? `≥ ${filters.minAltitude}m`
                : `≤ ${filters.maxAltitude}m`}
              <button onClick={() => setFilters(prev => ({ ...prev, minAltitude: '', maxAltitude: '' }))} className="hover:text-[#EF8354]/60">
                <XMarkIcon className="h-3 w-3" />
              </button>
            </span>
          )}
        </div>
      )}

      {/* ── Map ───────────────────────────────────────────────── */}
      <div className="mt-6 rounded-xl overflow-hidden shadow-lg border border-[#BFC0C0]/30 dark:border-[#4F5D75]/30">
        <div className="relative h-72 sm:h-80">
          <AirtimeMap
            ref={mapRef}
            initialViewState={{ longitude: 8.2, latitude: 46.8, zoom: 6 }}
            style={{ width: '100%', height: '100%' }}
            onLoad={handleMapLoad}
            onMoveEnd={handleMoveEnd}
            onClick={handleMapClick}
            interactiveLayerIds={['takeoff-circles']}
            cursor="pointer"
          >
            <NavigationControl position="top-right" />
            <Source id="takeoffs-src" type="geojson" data={geojson}>
              <Layer
                id="takeoff-circles"
                type="circle"
                paint={{
                  'circle-radius': 6,
                  'circle-color': '#EF8354',
                  'circle-stroke-color': '#ffffff',
                  'circle-stroke-width': 1.5,
                }}
              />
            </Source>
          </AirtimeMap>
          {/* Restrict-to-map toggle */}
          <button
            onClick={() => setRestrictToMap(v => !v)}
            title={restrictToMap ? 'Show all takeoffs' : 'Restrict list to map area'}
            className={`absolute top-3 left-3 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg shadow-md transition-colors ${
              restrictToMap
                ? 'bg-[#EF8354] text-white'
                : 'bg-white/90 dark:bg-[#2D3142]/90 text-[#4F5D75] dark:text-[#BFC0C0] hover:bg-white dark:hover:bg-[#2D3142]'
            }`}
          >
            <MapPinIcon className="h-4 w-4" />
            {restrictToMap ? 'Map area' : 'Restrict to map'}
          </button>
        </div>
      </div>

      {/* ── Takeoff list ──────────────────────────────────────── */}
      {isLoading ? (
        <div className="mt-6 animate-pulse space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-12 bg-[#BFC0C0]/30 rounded" />
          ))}
        </div>
      ) : takeoffs.length === 0 ? (
        <p className="mt-8 text-center text-sm text-[#4F5D75] dark:text-[#BFC0C0]">
          {isFiltered || restrictToMap ? 'No takeoff sites match the current filters.' : 'No takeoff sites yet.'}
        </p>
      ) : (
        <div className="mt-4 bg-white dark:bg-[#2D3142] shadow-lg rounded-lg overflow-hidden">
          {restrictToMap && (
            <div className="px-4 py-2 bg-[#EF8354]/5 border-b border-[#BFC0C0]/20 dark:border-[#4F5D75]/20">
              <span className="text-xs text-[#4F5D75] dark:text-[#BFC0C0]">
                Showing {takeoffs.length} of {allTakeoffs.length} takeoffs in map area
              </span>
            </div>
          )}
          <Table>
            <TableBody>
              {takeoffs.map(t => (
                <TableRow
                  key={t.id}
                  className="hover:bg-[#BFC0C0]/30 dark:hover:bg-[#4F5D75]/30 transition-colors cursor-pointer"
                  onClick={() => navigate(`/takeoff/${encodeURIComponent(t.name)}`)}
                >
                  <TableCell className="w-8">
                    <MiniExposition expositions={t.expositions ?? []} />
                  </TableCell>
                  <TableCell className="font-medium text-[#2D3142] dark:text-white">
                    <div className="flex items-center gap-2">
                      {t.country && (
                        <span className="text-base leading-none" title={t.country}>{countryFlag(t.country)}</span>
                      )}
                      <Link
                        to={`/takeoff/${encodeURIComponent(t.name)}`}
                        onClick={e => e.stopPropagation()}
                        className="text-[#EF8354] hover:underline"
                      >
                        {t.name}
                      </Link>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-[#4F5D75] dark:text-[#BFC0C0]">
                    {t.altitude}m
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <AddTakeoffModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={(name) => navigate(`/takeoff/${encodeURIComponent(name)}`)}
      />
    </div>
  )
}
